import {
    ArrowRight,
    Check,
    Info,
    MessageCirclePlus,
    TriangleAlert,
    X,
} from "lucide-react";
import { useState } from "react";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ChatAlertVariant = "information" | "blocker";

export interface ChatAlertProps {
    variant: ChatAlertVariant;
    message: string;
    /** Optional badge labels shown after the message text (e.g. "Budget", "Rooms"). */
    badges?: string[];
    /** Called when the user clicks Ask. The Ask button is shown only when this is provided. */
    onAsk?: () => void;
    /** Called when the user clicks the dismiss (X) button. Information variant only. */
    onDismiss?: () => void;
    className?: string;
}

// ---------------------------------------------------------------------------
// ChatAlert — compact single-row alert banner
// ---------------------------------------------------------------------------

export function ChatAlert({
    variant,
    message,
    badges,
    onAsk,
    onDismiss,
    className,
}: ChatAlertProps) {
    const isInformation = variant === "information";
    const showAsk = Boolean(onAsk);

    return (
        <Alert
            variant={isInformation ? "warning" : "destructive"}
            className={cn(
                "h-10 items-center gap-2 px-3 py-0",
                isInformation && "justify-between",
                className,
            )}
        >
            {/* Left: icon + message + optional badges */}
            <div className="flex min-w-0 items-center gap-1.5">
                {isInformation ? (
                    <Info className="size-4 shrink-0" />
                ) : (
                    <TriangleAlert className="size-4 shrink-0" />
                )}
                <span className="whitespace-nowrap font-medium text-xs">{message}</span>
                {badges && badges.length > 0 && (
                    <div className="ml-1 flex items-center gap-1">
                        {badges.map((badge) => (
                            <Badge key={badge} variant="amber">
                                {badge}
                            </Badge>
                        ))}
                    </div>
                )}
            </div>

            {/* Right: Ask (missing fields) + dismiss (information only) */}
            {(showAsk || (isInformation && onDismiss)) && (
                <div className="flex shrink-0 items-center gap-1">
                    {showAsk && (
                        <Button
                            size="xs"
                            onClick={onAsk}
                            aria-label="Ask for missing details"
                        >
                            <MessageCirclePlus strokeWidth={3} />
                            Ask
                        </Button>
                    )}
                    {isInformation && onDismiss && (
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={onDismiss}
                            aria-label="Dismiss alert"
                            className="-mr-1 shrink-0 text-amber-900 hover:bg-amber-100 hover:text-amber-900"
                        >
                            <X />
                        </Button>
                    )}
                </div>
            )}
        </Alert>
    );
}

// ---------------------------------------------------------------------------
// FieldUpdateSuggestion — detects field values in chat, requires explicit
// per-suggestion confirmation. Only fully dismisses when all are actioned.
// ---------------------------------------------------------------------------

export interface FieldSuggestion {
    /** Unique identifier for this suggestion. */
    id: string;
    /** The request field being updated (e.g. "Budget"). */
    field: string;
    /** Current value already on the request, if any. */
    currentValue?: string;
    /** Candidate value detected from the chat message. */
    suggestedValue: string;
}

type SuggestionStatus = "pending" | "confirmed" | "rejected";

export interface FieldUpdateSuggestionProps {
    /** Contextual hint shown at the top explaining why this surfaced. */
    reason: string;
    suggestions: FieldSuggestion[];
    className?: string;
}

export function FieldUpdateSuggestion({
    reason,
    suggestions,
    className,
}: FieldUpdateSuggestionProps) {
    const [statuses, setStatuses] = useState<Record<string, SuggestionStatus>>(() =>
        Object.fromEntries(suggestions.map((s) => [s.id, "pending"])),
    );

    const [expanded, setExpanded] = useState(true);

    const allActioned = suggestions.every((s) => statuses[s.id] !== "pending");
    const confirmed = suggestions.filter((s) => statuses[s.id] === "confirmed");
    const rejected = suggestions.filter((s) => statuses[s.id] === "rejected");
    const pending = suggestions.filter((s) => statuses[s.id] === "pending");

    function action(id: string, status: "confirmed" | "rejected") {
        setStatuses((prev) => ({ ...prev, [id]: status }));
    }

    // Minified state — all actioned and collapsed
    if (allActioned && !expanded) {
        return (
            <div className="flex justify-end">
                <Alert
                    variant="warning"
                    onClick={() => setExpanded(true)}
                    className={cn(
                        "size-10 cursor-pointer items-center justify-center p-0 transition-colors hover:bg-amber-100",
                        className,
                    )}
                    aria-label="Show update summary"
                >
                    <Info className="size-4 shrink-0" />
                </Alert>
            </div>
        );
    }

    return (
        <Alert
            variant="warning"
            className={cn("flex-col items-stretch gap-2 px-3 pt-2.5 pb-2", className)}
        >
            {/* Header */}
            <div className="flex items-center gap-1.5">
                <Info className="size-4 shrink-0" />
                <span className="font-medium text-xs">{reason}</span>
                <div className="ml-auto flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setExpanded(false)}
                        aria-label="Dismiss alert"
                        className={cn(
                            "-mr-1 shrink-0 text-amber-900 hover:bg-amber-100 hover:text-amber-900",
                            !allActioned && "pointer-events-none invisible",
                        )}
                    >
                        <X />
                    </Button>
                </div>
            </div>

            {/* Confirmed traces */}
            {confirmed.map((s) => (
                <div
                    key={s.id}
                    className="flex h-9 items-center gap-2 rounded-md border border-amber-100 bg-amber-50 px-2.5"
                >
                    <Badge variant="amber" className="shrink-0 opacity-60">
                        {s.field}
                    </Badge>
                    <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs">
                        {s.currentValue && (
                            <>
                                <span className="truncate text-zinc-400 line-through">
                                    {s.currentValue}
                                </span>
                                <ArrowRight className="size-3 shrink-0 text-zinc-400" />
                            </>
                        )}
                        <span className="truncate font-medium">{s.suggestedValue}</span>
                    </div>
                    <Check className="size-3.5 shrink-0" />
                </div>
            ))}

            {/* Rejected traces */}
            {rejected.map((s) => (
                <div
                    key={s.id}
                    className="flex h-9 items-center gap-2 rounded-md border border-amber-100 bg-amber-50 px-2.5 opacity-50"
                >
                    <Badge variant="amber" className="shrink-0 opacity-60">
                        {s.field}
                    </Badge>
                    <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs">
                        {s.currentValue && (
                            <>
                                <span className="truncate text-zinc-400 line-through">
                                    {s.currentValue}
                                </span>
                                <ArrowRight className="size-3 shrink-0 text-zinc-400" />
                            </>
                        )}
                        <span className="truncate line-through">
                            {s.suggestedValue}
                        </span>
                    </div>
                    <span className="shrink-0 text-xs">Dismissed</span>
                </div>
            ))}

            {/* Pending suggestions */}
            {pending.map((s) => (
                <div
                    key={s.id}
                    className="flex h-9 items-center gap-2 rounded-md border border-amber-100 bg-white px-2.5"
                >
                    <Badge variant="amber" className="shrink-0">
                        {s.field}
                    </Badge>
                    <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs">
                        {s.currentValue && (
                            <>
                                <span className="truncate text-zinc-400 line-through">
                                    {s.currentValue}
                                </span>
                                <ArrowRight className="size-3 shrink-0 text-zinc-400" />
                            </>
                        )}
                        <span className="truncate font-medium text-zinc-900">
                            {s.suggestedValue}
                        </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                        <Button
                            size="xs"
                            onClick={() => action(s.id, "confirmed")}
                            aria-label={`Confirm update: ${s.field} → ${s.suggestedValue}`}
                        >
                            <Check strokeWidth={3} />
                            Update
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => action(s.id, "rejected")}
                            aria-label={`Dismiss suggestion for ${s.field}`}
                            className="shrink-0 text-amber-900 hover:bg-amber-100 hover:text-amber-900"
                        >
                            <X />
                        </Button>
                    </div>
                </div>
            ))}
        </Alert>
    );
}

// ---------------------------------------------------------------------------
// ChatAlertMinified — collapsed icon-only state shown after dismissal
// ---------------------------------------------------------------------------

export function ChatAlertMinified({
    className,
    onExpand,
}: {
    className?: string;
    onExpand?: () => void;
}) {
    return (
        <Alert
            variant="warning"
            onClick={onExpand}
            onKeyDown={
                onExpand
                    ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onExpand();
                          }
                      }
                    : undefined
            }
            role={onExpand ? "button" : "alert"}
            tabIndex={onExpand ? 0 : undefined}
            aria-label={onExpand ? "Show alert" : undefined}
            className={cn(
                "size-10 items-center justify-center p-0",
                onExpand && "cursor-pointer transition-colors hover:bg-amber-100",
                className,
            )}
        >
            <Info className="size-4 shrink-0" />
        </Alert>
    );
}

// ---------------------------------------------------------------------------
// DismissibleChatAlert — stateful wrapper: expands to full alert, collapses
// to minified on dismiss
// ---------------------------------------------------------------------------

export interface DismissibleChatAlertProps {
    message: string;
    badges?: string[];
    onAsk?: () => void;
    className?: string;
}

export function DismissibleChatAlert({
    message,
    badges,
    onAsk,
    className,
}: DismissibleChatAlertProps) {
    const [dismissed, setDismissed] = useState(false);

    if (dismissed) {
        return (
            <div className="flex justify-end">
                <ChatAlertMinified
                    className={className}
                    onExpand={() => setDismissed(false)}
                />
            </div>
        );
    }

    return (
        <ChatAlert
            variant="information"
            message={message}
            badges={badges}
            onAsk={onAsk}
            onDismiss={() => setDismissed(true)}
            className={className}
        />
    );
}
